import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, DollarSign, Award, ChevronDown, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';

const CASE_STUDIES = [
  {
    id: 'manufacturing-fastening',
    sector: 'Manufacturing',
    industry: 'Fastening & Assembly Solutions',
    title: 'Revenue Tripled: ₹70 Cr → ₹200+ Cr in 3 Years',
    challenge: 'A ₹70 Cr global manufacturing firm faced internal bottlenecks that stalled progress. Roles lacked clarity, performance management was a tick-box activity, managers were under-equipped, collaboration was poor, and employees felt disengaged and detached from strategy.',
    solution: 'Delivered a comprehensive talent optimization strategy: defined KRAs and KPIs for 50+ roles (CEO to factory floor), aligned ERP to track real-time performance data, provided toolkits and training for managers, embedded a feedback-driven performance culture through monthly dialogues, and measured collaboration using 360 degree feedback.',
    results: [
      { metric: '186%', label: 'Revenue Growth' },
      { metric: '₹1.95 Cr', label: 'Cost Savings' },
      { metric: '₹80L', label: 'Inventory Savings' },
      { metric: '46%', label: 'Collection Time Reduced' }
    ],
    timeline: '3 years',
    color: '#E8642C'
  },
  {
    id: 'diamond-jewellery-kaizen',
    sector: 'Manufacturing',
    industry: 'Diamond Jewellery',
    title: '13.3% Unit Cost Reduction + 15% Productivity Boost',
    challenge: 'A globally renowned diamond jewellery manufacturer struggled with high gold waste, frequent reworks, prolonged production cycles, and low operational efficiency. These issues threatened on-time delivery, quality standards, and scalability amid rising global demand.',
    solution: 'Deployed a Kaizen-driven transformation focused on operational excellence and workforce empowerment: conducted detailed diagnostics, revamped the shop floor using 5S principles, developed practical SOPs, introduced real-time dashboards and daily floor walks, and upskilled employees across functions.',
    results: [
      { metric: '13.3%', label: 'Production Cost Cut' },
      { metric: '25%', label: 'Defect Reduction' },
      { metric: '15%', label: 'Efficiency Boost' },
      { metric: '70% → 85%', label: 'OEE Improvement' }
    ],
    timeline: 'Not specified',
    color: '#E8642C'
  },
  {
    id: 'agritech-engagement',
    sector: 'Technology',
    industry: 'Agri-Tech',
    title: '15% Engagement Increase in 12 Months',
    challenge: 'Leading electronic commodity spot market provider in India was modernizing agricultural trading but lagging internally. Faced one-way communication, blurred leadership roles, silos between departments, high junior attrition, and employees had little clarity on the company vision.',
    solution: 'Enabled a people-led transformation: rolled out company-wide surveys and feedback sessions, co-created action plans with employees, revamped PMS to embed collaboration, introduced SPOT Awards and Appreciation Circle, launched NHOP, and rolled out formal WFH policy.',
    results: [
      { metric: '15%', label: 'Engagement Increase' },
      { metric: '62% → 77%', label: 'Engagement Score' },
      { metric: '95.71%', label: 'Survey Participation' },
      { metric: 'Award', label: 'Most Healthy Workplace 2023' }
    ],
    timeline: '12 months',
    color: '#E8642C'
  },
  {
    id: 'diamond-leadership-excellence',
    sector: 'Manufacturing',
    industry: 'Diamond Jewellery',
    title: '20 Leaders Upskilled, 2 Promoted to CXO',
    challenge: 'Global diamond jewellery manufacturer faced a critical juncture post-COVID with bold target to double growth in 2 years. Existing managers struggled to juggle operational complexities, tight deadlines, and team development. Needed leaders who could inspire, delegate, and drive change.',
    solution: 'Designed the Leadership Excellence Program, a 9-month transformational journey: 4 power-packed leadership modules focused on real business challenges, Individual Development Plans (IDPs) for personalized growth, one-on-one executive coaching, post-program teach-backs, and bilingual delivery (English + Hindi).',
    results: [
      { metric: '2', label: 'CXO Promotions' },
      { metric: '20', label: 'Leaders Upskilled' },
      { metric: '6', label: 'Behavior Shifts' },
      { metric: '9 months', label: 'Program Duration' }
    ],
    timeline: '9 months',
    color: '#E8642C'
  },
  {
    id: 'plastics-leadership',
    sector: 'Manufacturing',
    industry: 'Plastics Processing',
    title: '₹1.95 Cr Savings + 86% Waste Reduction',
    challenge: 'India largest plastics processor rapidly expanded but faced critical talent challenge: equipping 11 home-grown leaders to handle greater responsibilities amid growing business complexity. Leaders needed to shift from operational excellence to strategic foresight.',
    solution: 'Designed a 12-month Leadership Excellence Program grounded in real-time development: Development Center & Coaching, Strategic Pit Stops (3-day sessions on Leading Self, People, and Business), Learning Nuggets, Live Team Projects, and Mid & Final Reviews.',
    results: [
      { metric: '₹1.95 Cr', label: 'Annual Savings' },
      { metric: '86%', label: 'Waste Reduced' },
      { metric: '5%', label: 'Profit Margin Increase' },
      { metric: '11', label: 'Leaders Developed' }
    ],
    timeline: '12 months',
    color: '#E8642C'
  },
  {
    id: 'diamond-workforce-productivity',
    sector: 'Manufacturing',
    industry: 'Diamond Jewellery Export',
    title: '12% Productivity Increase, 3% Attrition Drop',
    challenge: 'Top 10 diamond jewellery exporter in Asia faced growing inefficiencies despite global presence: rising labour costs, productivity dips, and seasonal burnout impacting profitability and morale. High workload during peak periods led to disengagement and attrition risks.',
    solution: 'Partnered to redesign workforce strategy: Daily Efficiency Routines (stand-up meetings, real-time scoreboards), Performance-Linked Pay, Strategic Workforce Planning, ESS (Employee Self Service) for automated dashboards, and Employee Wellbeing initiatives.',
    results: [
      { metric: '12%', label: 'Productivity Increase' },
      { metric: '15%', label: 'Top Performer Growth' },
      { metric: '15%', label: 'HR Time Saved' },
      { metric: '3%', label: 'Attrition Drop' }
    ],
    timeline: 'Not specified',
    color: '#E8642C'
  },
  {
    id: 'sleep-solutions-executive-search',
    sector: 'Consumer',
    industry: 'Sleep Solutions',
    title: '3 Critical Leadership Hires in 4 Months',
    challenge: 'Legacy-rich sleep solutions brand faced critical gap: absence of future-ready leadership to navigate rapidly evolving market. With shifting consumer needs and rising competition, needed visionary leaders who could drive innovation and align with family-owned culture.',
    solution: 'Led strategic executive search: Comprehensive Executive Search to identify and fill critical positions, Market Mapping & Candidate Profiling through deep industry research, Structured Assessments for leadership readiness and cultural compatibility, and Transparent Communication with stakeholders.',
    results: [
      { metric: '3', label: 'Leadership Hires' },
      { metric: '50+', label: 'Candidates Assessed' },
      { metric: '100%', label: 'Role-Culture Fit' },
      { metric: '4 months', label: 'Time to Hire' }
    ],
    timeline: '4 months',
    color: '#E8642C'
  },
  {
    id: 'retail-transformation',
    sector: 'Retail',
    industry: 'Fashion Retail',
    title: '130% to 34% Attrition + 25% Y-O-Y Revenue Growth',
    challenge: 'Youthful, fast-growing retail brand faced silent crisis despite aggressive expansion: 130%+ attrition, broken compensation model, disengaged employees, declining customer satisfaction, and operational wastage bleeding the business.',
    solution: 'Enabled business transformation: Visioning and Leadership Offsite, Workplace Uplift (hygiene and infrastructure), Smart Compensation (skill-linked, competency-based pay), Career Pathways (Career Passport, LaunchPad, LEAP), and Manager as Coach shift to engagement dialogues.',
    results: [
      { metric: '96 pp', label: 'Attrition Reduction' },
      { metric: '25%', label: 'Y-O-Y Revenue Growth' },
      { metric: '20%', label: 'Internal Hiring' },
      { metric: '<1%', label: 'Operational Loss' }
    ],
    timeline: 'Not specified',
    color: '#E8642C'
  },
  {
    id: 'insurance-leadership',
    sector: 'Financial Services',
    industry: 'Insurance',
    title: '250 Leaders Trained Over 1.5 Years',
    challenge: 'With entry of global competitors, insurance industry saw unprecedented disruption. Organization needed empowered leaders who could inspire thousands of insurance agents and foster internal collaboration to meet bold growth goals.',
    solution: 'Implemented "Why Should Anyone Be Led By You?" program based on research of Goffee and Jones. Focused on authentic leadership development targeting senior divisional and divisional managers to motivate, connect, and drive high performance.',
    results: [
      { metric: '250', label: 'Leaders Trained' },
      { metric: '10', label: 'Batches' },
      { metric: '1.5 years', label: 'Program Duration' },
      { metric: 'High', label: 'Team Energy Uplift' }
    ],
    timeline: '1.5 years',
    color: '#E8642C'
  },
  {
    id: 'retail-stagnation-turnaround',
    sector: 'Retail',
    industry: 'Retail',
    title: '70% Revenue Growth Over 3 Years',
    challenge: 'Company stagnated despite market opportunities. Teams lacked morale, growth was minimal, and internal alignment and ownership were missing. Priority was to energize the team, align efforts with clear vision, and build culture of accountability.',
    solution: 'Led structured transformation: co-created 3-year organizational vision with leadership, designed robust org structure, connected individual roles to business outcomes, implemented 12-month LEAP Leadership Program with 1:1 coaching, and drove organization-wide ownership.',
    results: [
      { metric: '70%', label: 'Revenue Growth' },
      { metric: '64% → 22%', label: 'New Hire Attrition' },
      { metric: '55% → 70%', label: 'Engagement' },
      { metric: '16%', label: 'Stable Attrition' }
    ],
    timeline: '3 years',
    color: '#E8642C'
  },
  {
    id: 'packaging-md-search',
    sector: 'Manufacturing',
    industry: 'Packaging',
    title: 'Visionary MD Hired in 90 Days',
    challenge: 'Client needed a visionary MD to lead Indian operations – someone with deep industry expertise, strategic acumen, and cultural fit to drive innovation and align with global goals.',
    solution: 'Worked on precision executive search strategy: defined ideal MD profile with global HQ, conducted extensive market research and competitor talent benchmarking, developed detailed candidate profiles emphasizing leadership capabilities, and performed deep competency evaluation.',
    results: [
      { metric: '90 days', label: 'Time to Closure' },
      { metric: '50+', label: 'Leaders Assessed' },
      { metric: '100%', label: 'Stakeholder Alignment' },
      { metric: 'High', label: 'Post-Hire Impact' }
    ],
    timeline: '90 days',
    color: '#E8642C'
  },
  {
    id: 'banking-adc',
    sector: 'Financial Services',
    industry: 'Banking',
    title: '1,175 Leaders Assessed and Developed',
    challenge: 'Bank aimed to build future-ready leadership pipeline but lacked structured tools to assess potential or align talent with business strategy. Key challenges: role-fit mismatches, unclear growth paths, limited feedback systems, and absence of competency-driven development plan.',
    solution: 'Deployed robust, competency-led talent strategy: mapped core, managerial, and leadership competencies, conducted structured interviews and FGDs, developed role-specific assessment tools, conducted virtual ADCs across 12 months, and shared detailed development plans for every participant.',
    results: [
      { metric: '1,175', label: 'Leaders Assessed' },
      { metric: '100%', label: 'Received IDPs' },
      { metric: '4', label: 'Job Scales (IV-VII)' },
      { metric: '12 months', label: 'Program Duration' }
    ],
    timeline: '12 months',
    color: '#E8642C'
  },
  {
    id: 'marble-giant-turnaround',
    sector: 'Manufacturing',
    industry: 'Marble',
    title: '₹400 Cr → ₹700 Cr in 18 Months (75% Growth)',
    challenge: 'Marble giant with strong market leadership and innovative products faced year-on-year revenue stagnation at ₹400 Cr. Outdated systems, leadership misalignment, skill gaps, no succession pipeline, and underdeveloped HR processes. Needed strategic reset.',
    solution: 'Partnered for transformative interventions: facilitated 2-day leadership retreat to align on vision, implemented Organizational Restructuring with new roles and transparent grade matrix, rolled out PMS system linked to KPIs, and facilitated Team Alignment Meetings (TAM).',
    results: [
      { metric: '75%', label: 'Revenue Growth' },
      { metric: '₹300 Cr', label: 'Value Created' },
      { metric: '5X', label: 'Market Outperformance' },
      { metric: '18 months', label: 'Timeline' }
    ],
    timeline: '18 months',
    color: '#E8642C'
  }
];

const SECTORS = ['All', 'Manufacturing', 'Retail', 'Financial Services', 'Technology', 'Consumer'];

export default function ImpactPage() {
  const [activeCaseStudy, setActiveCaseStudy] = useState(null);
  const [selectedSector, setSelectedSector] = useState('All');

  const filteredCaseStudies = selectedSector === 'All' 
    ? CASE_STUDIES 
    : CASE_STUDIES.filter(cs => cs.sector === selectedSector);

  useEffect(() => {
    // Check if URL has hash and scroll to it
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveCaseStudy(id);
        }
      }, 100);
    }
  }, []);

  return (
    <div className="overflow-hidden bg-[#FAF5EF]">
      {/* ═══════════════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-[#1A1008] via-[#2D1810] to-[#3D1C11] text-white overflow-hidden">
        {/* Decorative accent */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#F59E0B] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-[900px]">
              <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6" data-testid="impact-hero-label">
                Transformation Impact
              </p>
              <h1 className="text-[clamp(3rem,6vw,5.5rem)] font-bold leading-[1.05] tracking-[-0.02em] mb-8" data-testid="impact-hero-headline">
                Real businesses.<br/>Real results.
              </h1>
              <p className="text-xl text-white/80 leading-relaxed max-w-[700px] mb-12" data-testid="impact-hero-description">
                Transformation stories spanning manufacturing, retail, financial services, and technology. 
                Revenue growth. Cost savings. Leadership pipelines. Organizational efficiency. 
                Measurable ROI. Sustainable change.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-[900px]">
                <StatCounter end={739} suffix="+" label="Organizations" />
                <StatCounter end={275} suffix="%" label="Avg Revenue Growth" />
                <StatCounter end={1.95} suffix=" Cr+" label="Avg Cost Savings" decimals={2} />
                <StatCounter end={96.4} suffix="%" label="Client Satisfaction" decimals={1} />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTOR FILTER
          ═══════════════════════════════════════════════════════ */}
      <section className="sticky top-[72px] z-30 bg-white/95 backdrop-blur-md border-b border-[#3D1C11]/10 shadow-sm">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12 py-4">
          <div className="flex items-center gap-4 overflow-x-auto">
            <Filter size={18} className="text-[#6B5B50] flex-shrink-0" />
            <div className="flex gap-2">
              {SECTORS.map((sector) => (
                <button
                  key={sector}
                  onClick={() => setSelectedSector(sector)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    selectedSector === sector
                      ? 'bg-[#E8642C] text-white shadow-md'
                      : 'bg-white text-[#6B5B50] border border-[#3D1C11]/20 hover:border-[#E8642C]/40'
                  }`}
                  data-testid={`sector-filter-${sector.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {sector}
                </button>
              ))}
            </div>
            <div className="ml-auto text-sm text-[#6B5B50]">
              Showing {filteredCaseStudies.length}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          CASE STUDIES
          ═══════════════════════════════════════════════════════ */}
      <section className="py-16 lg:py-24 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="space-y-12">
            {filteredCaseStudies.map((caseStudy, index) => (
              <CaseStudyCard
                key={caseStudy.id}
                caseStudy={caseStudy}
                index={index}
                isActive={activeCaseStudy === caseStudy.id}
              />
            ))}
          </div>

          {filteredCaseStudies.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#6B5B50] text-lg">No case studies found for this sector.</p>
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          CTA BANNER
          ═══════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#1A1008] to-[#3D1C11] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#E8642C] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 mx-auto w-full max-w-[900px] px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6" data-testid="impact-cta-headline">
            Ready to write your transformation story?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-[700px] mx-auto" data-testid="impact-cta-description">
            Let's discuss how we can deliver measurable, sustainable impact for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#E8642C] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#CC2929] transition-all shadow-lg hover:shadow-xl"
              data-testid="impact-cta-contact-button"
            >
              Start a Conversation
              <ArrowRight size={16} />
            </Link>
            <a
              href="https://wa.me/919820574396?text=I%27d%20like%20to%20discuss%20a%20transformation%20project"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-white/20 transition-all border border-white/20"
              data-testid="impact-cta-whatsapp-button"
            >
              WhatsApp Us
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════

function StatCounter({ end, suffix, label, decimals = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  
  return (
    <div ref={ref} data-testid={`stat-counter-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
        {inView && <CountUp end={end} duration={2.5} decimals={decimals} />}
        {suffix}
      </div>
      <div className="text-xs text-white/60 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function CaseStudyCard({ caseStudy, index, isActive }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  
  return (
    <motion.div
      ref={ref}
      id={caseStudy.id}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: Math.min(index * 0.1, 0.3), ease: [0.16, 1, 0.3, 1] }}
      className={`scroll-mt-32 bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 ${
        isActive ? 'ring-2 ring-[#E8642C]' : ''
      }`}
      data-testid={`case-study-card-${caseStudy.id}`}
    >
      <div className="p-8 lg:p-12">
        <div className="grid lg:grid-cols-[1.2fr,0.8fr] gap-10 lg:gap-16">
          {/* Content */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="text-[10px] font-bold text-[#E8642C] tracking-[0.25em] uppercase">
                {caseStudy.sector}
              </div>
              <div className="w-1.5 h-1.5 rounded-full bg-[#3D1C11]/20" />
              <div className="text-[10px] font-medium text-[#6B5B50] tracking-[0.15em] uppercase">
                {caseStudy.industry}
              </div>
            </div>
            
            <h3 className="text-2xl lg:text-3xl font-bold text-[#1A1008] mb-8 leading-tight">
              {caseStudy.title}
            </h3>
            
            <div className="space-y-6">
              <div>
                <div className="text-sm font-bold text-[#1A1008] mb-2 flex items-center gap-2">
                  <div className="w-1 h-4 bg-[#E8642C] rounded-full" />
                  Challenge
                </div>
                <p className="text-[#6B5B50] leading-relaxed pl-3">{caseStudy.challenge}</p>
              </div>
              
              <div>
                <div className="text-sm font-bold text-[#1A1008] mb-2 flex items-center gap-2">
                  <div className="w-1 h-4 bg-[#E8642C] rounded-full" />
                  Solution
                </div>
                <p className="text-[#6B5B50] leading-relaxed pl-3">{caseStudy.solution}</p>
              </div>
              
              {caseStudy.timeline && caseStudy.timeline !== 'Not specified' && (
                <div className="pt-4 border-t border-[#3D1C11]/10">
                  <div className="text-xs font-medium text-[#6B5B50] uppercase tracking-wide">
                    Timeline: <span className="text-[#E8642C] font-bold">{caseStudy.timeline}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Results */}
          <div className="flex flex-col justify-center">
            <div className="bg-gradient-to-br from-[#FAF5EF] to-[#FFF7EF] rounded-2xl p-8 border border-[#3D1C11]/10">
              <div className="text-xs font-bold text-[#1A1008] mb-6 uppercase tracking-wide">
                Results
              </div>
              <div className="grid grid-cols-2 gap-6">
                {caseStudy.results.map((result, i) => (
                  <div key={i} className="text-center" data-testid={`case-study-result-${caseStudy.id}-${i}`}>
                    <div className="text-2xl lg:text-3xl font-bold text-[#E8642C] mb-2 leading-tight">
                      {result.metric}
                    </div>
                    <div className="text-[10px] text-[#6B5B50] uppercase tracking-wide leading-relaxed">
                      {result.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mt-6">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#E8642C] hover:gap-3 transition-all"
                data-testid={`case-study-cta-${caseStudy.id}`}
              >
                Discuss similar transformation
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function FadeIn({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}
